import { useId, useMemo, useState, type FormEvent } from 'react';
import {
  Banner,
  Button,
  Combobox,
  Dialog,
  FileField,
  Select,
  TextField,
  Textarea,
  type ComboboxOption,
  type FileFieldItem,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { createAfterSalesCase, useInvalidateTickets, usePropertyOptions, useUnitOptions } from '../api';
import styles from '../create/create.module.css';

const MAX_PHOTOS = 5;
const ACCEPT = 'image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf';

export interface CaseDraft {
  ticketId: string | null;
  title: string;
  description: string;
  propertyId: string | null;
  unitId: string | null;
}

export interface CreateCaseDialogProps {
  /** From a ticket („After-Sales-Vorgang anlegen …"): ticket, title and the ticket's unit are preset. */
  draft?: CaseDraft | undefined;
  /** Property filter of the board: preselects the object. */
  propertyId?: string | null | undefined;
  onClose: (createdId?: string) => void;
}

/**
 * „Vorgang anlegen" (After-Sales 03, no frame; own texts): Objekt → Einheit, Titel, Beschreibung, Fotos (≤ 5) →
 * `POST /after-sales` (multipart). From a ticket the case keeps the ticket's origin and takes its image attachments.
 */
export function CreateCaseDialog({ draft, propertyId: filterProperty, onClose }: CreateCaseDialogProps) {
  const formId = useId();
  const invalidate = useInvalidateTickets();
  const [propertyId, setPropertyId] = useState<string>(draft?.propertyId ?? filterProperty ?? '');
  const [unit, setUnit] = useState<ComboboxOption | null>(null);
  const [unitId, setUnitId] = useState<string | null>(draft?.unitId ?? null);
  const [title, setTitle] = useState(draft?.title ?? '');
  const [description, setDescription] = useState(draft?.description ?? '');
  const [photos, setPhotos] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const properties = usePropertyOptions(true);
  const units = useUnitOptions(propertyId || null);

  const unitOptions: ComboboxOption[] = useMemo(
    () =>
      (units.data?.items ?? []).map((item) => ({
        id: item.id,
        label: item.number,
        description: `${item.typeLabel} · ${item.statusLabel}`,
      })),
    [units.data],
  );
  const selectedUnitId = unit?.id ?? unitId;

  const photoItems: FileFieldItem[] = photos.map((file, index) => ({
    id: `${index}-${file.name}`,
    name: file.name,
    size: `${(file.size / 1_048_576).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`,
    progress: progress ?? undefined,
  }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!selectedUnitId) next['unitId'] = 'Bitte eine Einheit wählen.';
    if (!title.trim()) next['title'] = 'Bitte einen Titel angeben.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !selectedUnitId) return;
    setBanner(null);
    setProgress(0);
    try {
      const created = await createAfterSalesCase(
        {
          unitId: selectedUnitId,
          title: title.trim(),
          description,
          ticketId: draft?.ticketId ?? null,
          photos,
        },
        (fraction) => setProgress(Math.round(fraction * 100)),
      );
      await invalidate();
      onClose(created.id);
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length > 0) setErrors(error.fields);
      else setBanner(errorMessage(error));
      setProgress(null);
    }
  }

  const pending = progress !== null;
  return (
    <Dialog
      open
      onClose={() => onClose()}
      width={600}
      initialFocus="none"
      eyebrow="After-Sales"
      title={draft?.ticketId ? 'After-Sales-Vorgang aus dem Ticket' : 'Vorgang anlegen'}
      subtitle="Ein Mangel oder Gewährleistungsfall nach der Übergabe. Der Vorgang startet in „Gemeldet“."
      footer={
        <>
          <Button variant="secondary" onClick={() => onClose()} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Vorgang anlegen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <div className={styles.row}>
          <Select
            label="Objekt"
            required
            placeholder="Objekt wählen"
            value={propertyId}
            onChange={(event) => {
              setPropertyId(event.target.value);
              setUnit(null);
              setUnitId(null);
            }}
            options={(properties.data?.items ?? []).map((item) => ({ value: item.id, label: item.name }))}
          />
          <Combobox
            label="Einheit"
            required
            disabled={!propertyId}
            options={unitOptions}
            value={selectedUnitId}
            onChange={(_, option) => {
              setUnit(option);
              setUnitId(option?.id ?? null);
            }}
            placeholder={propertyId ? 'WE suchen' : 'Erst das Objekt wählen'}
            emptyText="Keine Einheit gefunden"
            error={errors['unitId']}
          />
        </div>
        <TextField
          label="Titel"
          required
          value={title}
          maxLength={200}
          onChange={(event) => setTitle(event.target.value)}
          error={errors['title']}
        />
        <Textarea
          label="Beschreibung"
          optional
          rows={3}
          autoGrow
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          error={errors['description']}
        />
        <FileField
          label="Fotos"
          buttonLabel="Fotos auswählen"
          description="JPG, PNG oder PDF bis 10 MB, höchstens fünf."
          accept={ACCEPT}
          multiple
          disabled={pending || photos.length >= MAX_PHOTOS}
          onFiles={(files) => setPhotos([...photos, ...files].slice(0, MAX_PHOTOS))}
          items={photoItems}
          onRemove={(item) =>
            setPhotos(photos.filter((_, index) => `${index}-${photos[index]?.name}` !== item.id))
          }
          error={errors['photos']}
        />
        {draft?.ticketId ? (
          <p className={styles.note}>Bilder aus dem Ticket werden als Fotos übernommen.</p>
        ) : null}
      </form>
    </Dialog>
  );
}
